import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import ResponsiveHeader from '../components/ResponsiveHeader';
import Footer from '../components/Footer';
import { Users, Target, TrendingUp, Award } from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitize';

const AboutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [pageData, setPageData] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/about-us`);
        if (response.ok) {
          const data = await response.json();
          setPageData(data);
        }
      } catch (error) {
        console.error('Error fetching about data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, [API_BASE]);

  // Build introduction content only (mission and vision are displayed in separate cards below)
  const buildContent = () => {
    if (!pageData) return "";
    
    let html = "";
    if (pageData.intro_paragraph_1) html += `<p>${pageData.intro_paragraph_1}</p>`;
    if (pageData.intro_paragraph_2) html += `<p>${pageData.intro_paragraph_2}</p>`;
    if (pageData.intro_paragraph_3) html += `<p>${pageData.intro_paragraph_3}</p>`;
    
    return html;
  };

  useEffect(() => {
    setContent(buildContent());
  }, [pageData]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <ResponsiveHeader />
      
      <main className="flex-1 pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          {/* Hero Section - Dynamic */}
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 sm:mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 dark:from-purple-400 dark:to-pink-400">
                {pageData?.page_title || "About Us"}
              </span>
            </h1>
          </div>

        {/* Introduction - Dynamic */}
        {content && (
          <Card className="mb-8 border-2 border-red-200 dark:border-gray-700 shadow-xl dark:bg-gray-800">
            <CardContent className="pt-6">
              <div 
                className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
              />
            </CardContent>
          </Card>
        )}

        {/* Fallback: Show default content if no API data */}
        {!content && !loading && (
          <Card className="mb-8 border-2 border-red-200 dark:border-gray-700 shadow-xl dark:bg-gray-800">
            <CardContent className="pt-6">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg mb-4">
                Founded in Toronto, Canada in 2022, <span className="font-bold text-red-700 dark:text-red-400">TrainerList</span> is an innovative digital platform redefining the way fitness professionals and clients connect.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg mb-4">
                In an era where health and fitness have become essential components of personal well-being, the challenge of accessing qualified and specialized trainers remains significant.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                We're solving this by creating a digital ecosystem where trainers gain visibility, clients discover the perfect match, and the entire fitness community thrives.
              </p>
            </CardContent>
          </Card>
        )}

          {/* Mission & Vision - Dynamic */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 sm:mb-12">
            {/* Our Mission - Dynamic */}
            <Card className="border-2 border-red-200 dark:border-gray-700 shadow-xl hover:shadow-2xl transition-shadow duration-300 dark:bg-gray-800">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-gradient-to-r from-red-600 to-red-800 rounded-full mr-4">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
                    {pageData?.mission_title || "Our Mission"}
                  </h2>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  {pageData?.mission_text || "To empower fitness professionals with the visibility and tools they need to grow, while simplifying the process for clients to find trusted, specialized trainers. We believe in fostering a community that prioritizes health, expertise, and authenticity."}
                </p>
              </CardContent>
            </Card>

            {/* Our Vision - Dynamic */}
            <Card className="border-2 border-red-300 dark:border-gray-700 shadow-xl hover:shadow-2xl transition-shadow duration-300 dark:bg-gray-800">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-gradient-to-r from-red-700 to-gray-800 rounded-full mr-4">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
                    {pageData?.vision_title || "Our Vision"}
                  </h2>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  {pageData?.vision_text || "To become the leading global platform for personal training connections—where trainers are empowered to scale their businesses, and clients experience world-class fitness guidance, all in one trusted ecosystem."}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Strategic Goals - Dynamic */}
          <Card className="mb-8 sm:mb-12 border-2 border-red-200 dark:border-gray-700 shadow-xl dark:bg-gray-800">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-red-600 to-red-800 rounded-full mr-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
                  {pageData?.strategic_goals_title || "Strategic Goals"}
                </h2>
              </div>
              <div className="space-y-4">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  {pageData?.strategic_goals_paragraph_1 || "In our initial phase, our priority is to cultivate a robust community within the Canadian fitness sector, establishing TrainerList as a trusted and recognizable brand among professionals and consumers alike."}
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  {pageData?.strategic_goals_paragraph_2 || "Over the long term, our vision extends globally — positioning TrainerList at the forefront of online fitness engagement, digital wellness marketing, and community-driven health innovation."}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Why TrainerList - Dynamic */}
          <Card className="mb-8 sm:mb-12 border-2 border-red-300 dark:border-gray-700 shadow-xl dark:bg-gray-800">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-red-700 to-gray-800 rounded-full mr-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
                  {pageData?.why_title || "Why TrainerList?"}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-gradient-to-br from-red-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-red-100 dark:border-gray-600">
                  <div className="text-3xl mb-3">🏆</div>
                  <h3 className="font-bold text-xl mb-2 text-red-700 dark:text-red-400">
                    {pageData?.why_point_1_title || "For Trainers"}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {pageData?.why_point_1_text || "Amplify your professional presence, attract quality clients, and grow your business with a platform designed specifically for fitness experts."}
                  </p>
                </div>
                <div className="p-6 bg-gradient-to-br from-red-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-red-200 dark:border-gray-600">
                  <div className="text-3xl mb-3">💪</div>
                  <h3 className="font-bold text-xl mb-2 text-red-700 dark:text-red-400">
                    {pageData?.why_point_2_title || "For Clients"}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {pageData?.why_point_2_text || "Find trainers who truly match your fitness goals, lifestyle, and preferences—all in one place, backed by verified credentials and real reviews."}
                  </p>
                </div>
                <div className="p-6 bg-gradient-to-br from-red-50 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-red-100 dark:border-gray-600">
                  <div className="text-3xl mb-3">🌍</div>
                  <h3 className="font-bold text-xl mb-2 text-red-700 dark:text-red-400">
                    {pageData?.why_point_3_title || "For the Community"}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {pageData?.why_point_3_text || "We're creating a network effect where trainers and clients lift each other up—fostering a healthier, more connected world."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Feature Cards - Not Dynamic (hardcoded) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 sm:mb-12">
            <Card className="border-2 border-red-200 dark:border-gray-700 shadow-xl dark:bg-gray-800">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-3">For Fitness Professionals</h3>
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">✓</span>
                    <span>Showcase your expertise through detailed profiles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">✓</span>
                    <span>Connect with clients actively seeking your specialization</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 dark:text-purple-400 mr-2">✓</span>
                    <span>Build your brand with testimonials and verified ratings</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-300 dark:border-gray-700 shadow-xl dark:bg-gray-800">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-3">For Clients</h3>
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-pink-600 dark:text-pink-400 mr-2">✓</span>
                    <span>Discover trainers by location, expertise, and price range</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-pink-600 dark:text-pink-400 mr-2">✓</span>
                    <span>Read authentic reviews from real clients</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-pink-600 dark:text-pink-400 mr-2">✓</span>
                    <span>Book sessions with confidence and ease</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

        {/* Closing Statement - Dynamic */}
        <Card className="border-2 border-red-200 dark:border-gray-700 shadow-xl bg-gradient-to-r from-red-50 to-gray-200 dark:from-gray-800 dark:to-gray-700">
          <CardContent className="pt-6 pb-6">
            <p className="text-center text-gray-800 dark:text-gray-200 text-lg font-semibold leading-relaxed">
              {pageData?.closing_statement || "At TrainerList, we are committed to setting new standards in the global fitness industry — delivering exceptional value, fostering meaningful connections, and shaping the future of personal wellness."}
            </p>
          </CardContent>
        </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
